// assets/edm-bundle-cart-manager.js
// Cart Manager - Handles cart synchronization and MutationObserver management
// Context7: Manager Pattern - Separates cart sync concerns from Controller

window.EdumonBundle = window.EdumonBundle || {};

window.EdumonBundle.CartManager = {
  /**
   * Initialize cart manager
   * @param {Object} config - Configuration
   * @param {number} config.productId - Product ID
   * @param {Object} config.state - State instance (by reference)
   * @param {Array} config.tiers - Discount tiers
   * @param {number} config.maxSlots - Maximum slots
   * @param {Element} config.el - Bundle builder element
   * @param {Function} config.updateUI - Update UI function
   * @param {Function} config.onCartEmpty - Callback when cart is empty (optional)
   * @param {Function} config.onMobileLoadingHide - Callback to hide mobile loading (optional)
   * @param {Function} config.onMobileSheetReset - Callback to reset mobile sheet (optional)
   * @returns {Object} Manager instance
   */
  init: function(config) {
    const Config = window.EdumonBundle.Config;
    const API = window.EdumonBundle.API;
    const Utils = window.EdumonBundle.Utils;
    
    const {
      productId,
      state,
      tiers,
      maxSlots,
      el,
      updateUI,
      onCartEmpty,
      onMobileLoadingHide,
      onMobileSheetReset
    } = config;
    
    // Instance ID for sync state management
    const instanceId = String(productId);
    
    // Per-instance sync state (prevents race conditions)
    // Context7: State Sharing - syncState is managed per instance, not duplicated
    const syncState = { syncing: false, syncVersion: 0 };
    
    // AUDIT FIX: Store observers for cleanup
    let drawerObserver = null;
    let cartObserver = null;
    let moTimer = null;
    let userClosedDuringSync = false;
    
    // Setup drawer observer for user-close-during-sync detection
    const drawer = document.querySelector(Config.SELECTORS.CART_DRAWER);
    if (drawer) {
      drawerObserver = new MutationObserver(() => {
        if (syncState.syncing && !drawer.hasAttribute('open')) {
          userClosedDuringSync = true;
        }
      });
      drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }
    
    // Setup cart drawer observer for state sync
    const cartDrawerObserverTarget = document.querySelector(Config.SELECTORS.CART_DRAWER);
    if (cartDrawerObserverTarget) {
      cartObserver = new MutationObserver(() => {
        // AUDIT FIX: Clear timer on new mutation
        if (moTimer) clearTimeout(moTimer);
        
        moTimer = setTimeout(async () => {
          // Context7: SOT - Loading screen is handled by MobileManager.handleCommit()
          // Do NOT hide loading screen here - it causes premature hiding before sync completes
          if (!cartDrawerObserverTarget.hasAttribute('open')) {
            // Check if user closed during sync
            if (syncState.syncing) {
              userClosedDuringSync = true;
            }
          }
          
          // Don't sync if already syncing or sheet is locked
          if (syncState.syncing || document.body.classList.contains(Config.CLASSES.LOCK)) {
            return;
          }
          
          // Fetch fresh cart state
          const cart = await API.fetchCart();
          const items = cart.items || [];
          
          // Handle empty cart
          if (!items.length) {
            state.reset();
            if (onMobileSheetReset) onMobileSheetReset();
            if (updateUI) updateUI();
            // Fire cart:empty event
            document.dispatchEvent(new CustomEvent('cart:empty', { detail: { productId } }));
            if (onCartEmpty) onCartEmpty();
            return;
          }
          
          // Check if bundle of this product still exists
          const groups = await API.readAllBundleGroupsOfProduct(productId);
          const hasAnyOfThisProduct = [...groups.values()].some(g => g.totalQty > 0);
          
          if (!hasAnyOfThisProduct) {
            state.reset();
            if (onMobileSheetReset) onMobileSheetReset();
            if (updateUI) updateUI();
            // Fire cart:empty event
            document.dispatchEvent(new CustomEvent('cart:empty', { detail: { productId } }));
            if (onCartEmpty) onCartEmpty();
          }
        }, Config.CONSTANTS.MUTATION_OBSERVER_DELAY_MS);
      });
      
      cartObserver.observe(cartDrawerObserverTarget, { attributes: true, attributeFilter: ['open'] });
    }
    
    /**
     * Load bundle from cart into state
     * @returns {Promise<Object|null>} Bundle group or null
     */
    const loadFromCart = async function() {
      // Context7: Use API.loadBundleFromCart() which handles state updates internally
      const grp = await API.loadBundleFromCart(productId, state, state.activeBundleKey);
      
      // Reset mobile sheet if empty
      if (!grp && Utils.isMobile() && onMobileSheetReset) {
        onMobileSheetReset();
      }
      
      return grp;
    };
    
    /**
     * Sync bundle state to cart
     * @returns {Promise<boolean>} Success status
     */
    const syncToCart = async function() {
      // Prevent concurrent syncs
      if (syncState.syncing) return false;
      
      syncState.syncing = true;
      const v = ++syncState.syncVersion;
      
      const isStale = (version) => version !== syncState.syncVersion;
      
      try {
        // Input validation & maximum enforcement
        const desiredMap = new Map(
          Object.entries(state.items)
            .filter(([,q]) => q > 0)
            .map(([vid,q]) => [Number(vid), q])
        );
        let totalQty = Array.from(desiredMap.values()).reduce((a,b)=>a+b,0);
        
        if (totalQty > maxSlots) {
          console.warn('[Bundle Builder] Total quantity exceeds maximum, capping to', maxSlots);
          const cappedItems = Utils.capToMaximum(state.items, maxSlots);
          state.setItems(cappedItems);
          desiredMap.clear();
          for (const [vid, qty] of Object.entries(state.items).filter(([,q]) => q > 0)) {
            desiredMap.set(Number(vid), qty);
          }
          totalQty = Array.from(desiredMap.values()).reduce((a,b)=>a+b,0);
        }
        
        if (isStale(v)) {
          syncState.syncing = false;
          return false;
        }
        
        // Tier calculation
        let currentTier = null;
        for (const t of tiers) if (totalQty >= t.qty) currentTier = t;
        const newTierStr = currentTier ? `${currentTier.qty}@${currentTier.off}` : 'none';
        
        // Get current cart state
        const cart = await API.fetchCart();
        if (isStale(v)) {
          syncState.syncing = false;
          return false;
        }
        
        // Analyze cart: Find all items of this product
        const cartItemsByVariant = new Map();
        for (const item of (cart.items || [])) {
          if (Number(item.product_id) !== productId) continue;
          const vid = Number(item.variant_id);
          const props = item.properties || {};
          const itemBundleKey = props._bundle_key || props.bundle_key || null;
          if (!cartItemsByVariant.has(vid)) cartItemsByVariant.set(vid, []);
          cartItemsByVariant.get(vid).push({
            key: item.key,
            quantity: item.quantity,
            properties: props,
            bundleKey: itemBundleKey
          });
        }
        
        // Determine bundle key
        let bundleKey = state.activeBundleKey || null;
        if (!bundleKey && totalQty > 0) {
          const existing = await API.readExistingBundleGroupOfProduct(productId);
          bundleKey = existing?.bundleKey || `B-${Date.now()}`;
        }
        
        // Calculate diff
        const itemsToAdd = [];
        const updates = {};
        
        for (const [vid, desiredQty] of desiredMap.entries()) {
          const cartLines = cartItemsByVariant.get(vid) || [];
          const linesByBundleKey = new Map();
          for (const line of cartLines) {
            const bkey = line.bundleKey || 'standalone';
            if (!linesByBundleKey.has(bkey)) linesByBundleKey.set(bkey, []);
            linesByBundleKey.get(bkey).push(line);
          }
          
          let bundleLines = [];
          if (bundleKey) {
            const activeBundleLines = linesByBundleKey.get(bundleKey) || [];
            const standaloneLines = linesByBundleKey.get('standalone') || [];
            const otherBundleLines = [];
            for (const [bkey, lines] of linesByBundleKey.entries()) {
              if (bkey !== bundleKey && bkey !== 'standalone') otherBundleLines.push(...lines);
            }
            bundleLines = activeBundleLines.concat(standaloneLines).concat(otherBundleLines);
          } else {
            bundleLines = linesByBundleKey.get('standalone') || [];
          }
          
          bundleLines.sort((a, b) => a.key.localeCompare(b.key));
          const currentQty = bundleLines.reduce((sum, line) => sum + line.quantity, 0);
          const headLine = bundleLines.length > 0 ? bundleLines[0] : null;
          const headHasCorrectProps = headLine && 
            headLine.bundleKey === bundleKey && 
            (headLine.properties?._bundle_tier || headLine.properties?.bundle_tier) === newTierStr;
          
          if (bundleLines.length === 0) {
            itemsToAdd.push({
              id: vid,
              quantity: desiredQty,
              properties: {
                _bundle_key: bundleKey,
                _bundle_tier: newTierStr
              }
            });
          } else if (bundleLines.length === 1 && headHasCorrectProps && currentQty === desiredQty) {
            // Perfect match - skip
          } else {
            for (const line of bundleLines) updates[line.key] = 0;
            itemsToAdd.push({
              id: vid,
              quantity: desiredQty,
              properties: {
                _bundle_key: bundleKey,
                _bundle_tier: newTierStr
              }
            });
          }
        }
        
        // Remove variants
        for (const [vid, cartLines] of cartItemsByVariant.entries()) {
          if (desiredMap.has(vid)) continue;
          const bundleLines = cartLines.filter(line => {
            const lineBundleKey = line.bundleKey || 'standalone';
            const targetBundleKey = bundleKey || 'standalone';
            return lineBundleKey === targetBundleKey;
          });
          for (const line of bundleLines) updates[line.key] = 0;
        }
        
        // Handle other bundles
        if (bundleKey && totalQty > 0) {
          for (const [vid, cartLines] of cartItemsByVariant.entries()) {
            if (desiredMap.has(vid)) continue;
            const otherBundleLines = cartLines.filter(line => {
              const lineBundleKey = line.bundleKey || null;
              return lineBundleKey && lineBundleKey !== bundleKey;
            });
            for (const line of otherBundleLines) updates[line.key] = 0;
          }
        }
        
        // Special case: Remove all items
        if (totalQty === 0) {
          const allProductLines = [];
          for (const lines of cartItemsByVariant.values()) allProductLines.push(...lines);
          for (const line of allProductLines) updates[line.key] = 0;
          itemsToAdd.length = 0;
        }
        
        // Batch execution
        const cartEl = document.querySelector(Config.SELECTORS.CART_NOTIFICATION) || 
                       document.querySelector(Config.SELECTORS.CART_DRAWER);
        const RENDER_SECTIONS = API.getSectionsToRender();
        let finalSections = null;
        
        // Step 1: Update existing items
        if (Object.keys(updates).length > 0) {
          const { data } = await API.cartUpdateWithSections(updates);
          if (isStale(v)) {
            syncState.syncing = false;
            return false;
          }
          finalSections = data?.sections || null;
        }
        
        // Step 2: Add new items
        if (itemsToAdd.length > 0) {
          const data = await API.cartAdd(itemsToAdd, RENDER_SECTIONS);
          if (isStale(v)) {
            syncState.syncing = false;
            return false;
          }
          finalSections = data?.sections || finalSections;
        }
        
        // Update properties
        if (bundleKey && totalQty > 0) {
          const freshCart = await API.fetchCart();
          if (isStale(v)) {
            syncState.syncing = false;
            return false;
          }
          
          const propUpdatePromises = [];
          for (const item of (freshCart.items || [])) {
            if (Number(item.product_id) !== productId) continue;
            if (!desiredMap.has(Number(item.variant_id))) continue;
            const props = item.properties || {};
            const itemBundleKey = props._bundle_key || props.bundle_key || null;
            const itemTier = props._bundle_tier || props.bundle_tier || null;
            const needsKeyUpdate = itemBundleKey !== bundleKey;
            const needsTierUpdate = itemTier !== newTierStr;
            if (needsKeyUpdate || needsTierUpdate) {
              const updatedProps = Object.assign({}, props);
              updatedProps._bundle_key = bundleKey;
              updatedProps._bundle_tier = newTierStr;
              propUpdatePromises.push(API.setLineProps(item.key, updatedProps));
            }
          }
          if (propUpdatePromises.length > 0) await Promise.all(propUpdatePromises);
        }
        
        // Render sections
        if (finalSections) {
          await API.handleSectionsWithVerification({ sections: finalSections }, 'bb-sync');
        } else if (cartEl?.renderContents && Array.isArray(RENDER_SECTIONS) && RENDER_SECTIONS.length) {
          const url = `${window.location.pathname}?sections=${encodeURIComponent(RENDER_SECTIONS.join(','))}`;
          const res2 = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          const json2 = await res2.json();
          await API.handleSectionsWithVerification({ sections: json2 }, 'bb-sync-refresh');
        }
        
        // Update state from server
        if (totalQty === 0) {
          state.reset();
        } else {
          state.setActiveBundleKey(bundleKey);
          state.markClean();
        }
        
        if (updateUI) updateUI();
        
        syncState.syncing = false;
        return true;
        
      } catch (error) {
        console.error('[Bundle Builder] syncCartBundleToState error:', error);
        API.invalidateCache();
        await loadFromCart();
        if (updateUI) updateUI();
        syncState.syncing = false;
        return false;
      } finally {
        API.invalidateCache();
      }
    };
    
    // Return manager instance
    return {
      // Public API
      syncToCart,
      loadFromCart,
      
      // Internal state access (for Controller)
      getSyncState: () => syncState,
      getUserClosedDuringSync: () => userClosedDuringSync,
      
      // AUDIT FIX: Cleanup method
      destroy: function() {
        // Disconnect observers
        if (drawerObserver) {
          drawerObserver.disconnect();
          drawerObserver = null;
        }
        if (cartObserver) {
          cartObserver.disconnect();
          cartObserver = null;
        }
        
        // Clear timer
        if (moTimer) {
          clearTimeout(moTimer);
          moTimer = null;
        }
      }
    };
  },
  
  /**
   * Add items to cart (static method for Landing Pages)
   * Context7: Architectural Consistency - Unifies cart API usage
   * 
   * @param {Array} items - Items array [{id: variantId, quantity: qty}]
   * @returns {Promise<Object>} {success: boolean, error?: Error, data?: Object}
   */
  addItems: async function(items) {
    const Config = window.EdumonBundle.Config;
    const API = window.EdumonBundle.API;
    
    try {
      // Get sections to render
      const RENDER_SECTIONS = API.getSectionsToRender();
      
      // Add items to cart
      const data = await API.cartAdd(items, RENDER_SECTIONS);
      
      // Check for Shopify API errors (cartAdd returns JSON even on error)
      if (data?.description || data?.status) {
        // Shopify returns error in description field
        const errorMessage = data.description || Config.TEXTS.ERROR_ADD_CART;
        return { success: false, error: new Error(errorMessage) };
      }
      
      // Update cart drawer if sections are available
      if (data?.sections) {
        await API.handleSectionsWithVerification({ sections: data.sections }, 'bb-add-items');
      } else {
        // Fallback: refresh cart drawer
        const cartDrawer = document.querySelector(Config.SELECTORS.CART_DRAWER);
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          let sectionsRoute = Config.ROUTES.SECTIONS(['cart-drawer', 'cart-icon-bubble']);
          if (window.Shopify?.routes?.root) {
            sectionsRoute = window.Shopify.routes.root + sectionsRoute.substring(1);
          }
          fetch(sectionsRoute)
            .then(r => r.json())
            .then(sections => {
              if (cartDrawer.renderContents) {
                cartDrawer.renderContents(sections);
              }
            });
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      }
      
      return { success: true, data };
      
    } catch (error) {
      console.error('[CartManager] Error adding items to cart:', error);
      const errorMessage = error.message || Config.TEXTS.ERROR_ADD_CART;
      return { success: false, error: new Error(errorMessage) };
    }
  }
};
